import React, {useState} from "react";
import {useAppDispatch, useAppSelector} from "../../app/hooks";
import {deletePushNotification, selectIsLoading, selectIsLoadingMore, selectIsUpdating} from "../../redux/pushNotification/pushNotificationListSlice";
import {ColumnProps} from "rc-table/lib/sugar/Column";
import {Heading4, Paragraph, TextStyles} from "../text";
import Moment from "moment";
import Table from "rc-table";
import {Button} from "../buttons/Button";
import {useModalRouting} from "../../utils/useModalRouting";
import {PushNotificationModel} from "../../models/pushNotification";
import {TableHeader} from "../table/TableHeader";
import {TableCell} from "../table/TableCell";
import {ArrowDownIcon} from "../icons";
import {PushNotificationDetailSendModal} from "./PushNotificationDetailSendModal";
import {AlertDialog} from "../dialogs/AlertDialog";
import {useRouter} from "next/router";

export interface PushNotificationsTableProps {
  pushNotifications: PushNotificationModel[]
  onLoadMoreClicked: () => void
}

const PushNotificationTableActionButton: React.FC<{ pushNotification: PushNotificationModel }> = ({ pushNotification }) => {
  const isUpdating = useAppSelector(state => selectIsUpdating(state, pushNotification.id))
  const dispatch = useAppDispatch()
  const { createRoute } = useModalRouting()
  const router = useRouter()

  const [sendModalOpen, setSendModalOpen] = useState({ isOpen: false, isTest: true })
  const [pushNotificationIdToDelete, setPushNotificationIdToDelete] = useState<string>()

  return <div className="relative">
    <div className="absolute top-0 left-0">
      <Button
        isLoading={isUpdating}
        buttonStyle="secondary"
        trailingIconSizeClass="h-3 w-3"
        trailingicon={<ArrowDownIcon />}>
        <span className="capitalize">Actions</span>
      </Button>
    </div>

    <select
      id="actions"
      name="actions"
      value={'blank'}
      onClick={e => {
        e.stopPropagation()
      }}
      onChange={e => {
        if (!e.target.value) return

        const pushNotificationId = pushNotification.id
        if (!pushNotificationId) return

        switch (e.target.value) {
          case 'edit':
            router.push(
              createRoute({ name: 'push_notification_detail', query: { push_notification_id: pushNotification.id }, returnTo: '/admin/push-notifications' }),
              undefined,
              { shallow: true }
            )
            break;
          case 'delete':
            setPushNotificationIdToDelete(pushNotification.id)
            break;
          case 'send_push':
            setSendModalOpen({ isOpen: true, isTest: false })
            break;
          case 'send_test':
            setSendModalOpen({ isOpen: true, isTest: true })
            break;
        }
      }}
      className={`appearance-none opacity-0 transition-colors bg-fg-primary hover:bg-fg-gray-surface-dark border-0 cursor-pointer text-center focus:ring-0 rounded-full px-5 py-2 flex flex-row items-center ${TextStyles.Body}`}
    >
      <option key={'blank'} value={'blank'}/>
      <option key={'edit'} value={'edit'}>Edit</option>
      <option key={'delete'} value={'delete'}>Delete</option>
      <option key={'send_push'} value={'send_push'}>Send Push</option>
      <option key={'send_test'} value={'send_test'}>Send Test</option>
    </select>

    <AlertDialog
      isOpen={!!pushNotificationIdToDelete}
      message={'Are you sure you want to delete this push notification?'}
      title={'Delete Push Notification?'}
      buttons={[
        {
          title: { key: 'common:common_title_delete' },
          onClick: () => {
            pushNotificationIdToDelete && dispatch(deletePushNotification(pushNotificationIdToDelete))
            setPushNotificationIdToDelete(undefined)
          },
          style: 'destructive'
        },
        {
          title: { key: 'common:common_title_cancel' },
          onClick: () => setPushNotificationIdToDelete(undefined),
          style: 'cancel'
        }
      ]}
    />

    <PushNotificationDetailSendModal
      isOpen={sendModalOpen.isOpen}
      pushNotification={pushNotification}
      isTest={sendModalOpen.isTest}
      onClose={() => {
        setSendModalOpen({ isOpen: false, isTest: true })
      }}
    />
  </div>
}

export const PushNotificationsTable: React.FC<PushNotificationsTableProps> = ({ pushNotifications, onLoadMoreClicked }) => {
  const isLoading = useAppSelector(selectIsLoading)
  const isLoadingMore = useAppSelector(selectIsLoadingMore)

  const columns: ColumnProps<PushNotificationModel>[] = [
    {
      title: <TableHeader isFirst={true}>
        <Heading4 className="text-fg-gray-dark">Title</Heading4>
      </TableHeader>,
      dataIndex: 'title',
      render: (text, record) => (
        <TableCell>
          <Paragraph>{`${record.name ?? record.title}`}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Create On</Heading4>
      </TableHeader>,
      dataIndex: 'created_at',
      render: (text, record) => (
        <TableCell>
          <Paragraph>{Moment(record.createdAt).format('MMM D, YYYY')}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Deliveries</Heading4>
      </TableHeader>,
      dataIndex: 'deliveries',
      render: (text, record) => (
        <TableCell>
          <Paragraph>{record.stats?.delivered ?? 0}</Paragraph>
        </TableCell>
      )
    },
    {
      title: <TableHeader>
        <Heading4 className="text-fg-gray-dark">Open %</Heading4>
      </TableHeader>,
      dataIndex: 'open_rate',
      render: (text, record) => {
        let deliveries = record.stats?.delivered ?? 0
        deliveries = deliveries ? deliveries : 1
        const opens = record.stats?.opens ?? 0

        const openRate = Math.min(opens / deliveries, 1)
        const openRatePercentage = Math.round(openRate * 100)

        return <TableCell>
          <Paragraph>{openRatePercentage}%</Paragraph>
        </TableCell>
      }
    },
    {
      title: <TableHeader isLast={true}>

      </TableHeader>,
      dataIndex: 'action',
      render: (text, record) => (
        <TableCell>
          <PushNotificationTableActionButton pushNotification={record} />
        </TableCell>
      )
    }
  ]

  return <Table
    className="rounded-lg border border-fg-gray-surface fg-table"
    scroll={{x: true}}
    data={pushNotifications}
    columns={columns}
    emptyText={() => {
      return <div className="w-full h-16 flex items-center justify-center">
        <Paragraph>No Push Notifications</Paragraph>
      </div>
    }}
    footer={() => {
      return <div className="w-full flex items-center justify-center py-8 border-t border-fg-gray-surface">
        <Button
          buttonStyle="secondary"
          isLoading={isLoadingMore}
          onClick={() => {
            onLoadMoreClicked()
          }}
        >
          Load More
        </Button>
      </div>
    }}
  />
}